import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, formatPrice } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function defaultRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), 0, 1);
  return { from: start.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}

export default function FinancialsTab() {
  const [range, setRange] = useState(defaultRange());
  const { data, refetch } = useQuery({
    queryKey: ["financials", range.from, range.to],
    queryFn: async () => (await api.get(`/admin/financials/summary?from_date=${range.from}&to_date=${range.to}`)).data,
  });

  if (!data) {
    return <div className="text-center text-gg-muted py-12">Loading financials…</div>;
  }

  // Build monthly profit chart from classes + bookings
  const monthly = {};
  for (const c of data.classes || []) {
    const m = (c.class_date || "").slice(0, 7);
    if (!m) continue;
    monthly[m] = monthly[m] || { month: m, revenue: 0, expenses: 0, profit: 0 };
    monthly[m].revenue += c.revenue;
    monthly[m].expenses += c.expenses;
    monthly[m].profit += c.profit;
  }
  for (const b of data.bookings || []) {
    const m = (b.date || "").slice(0, 7);
    if (!m) continue;
    monthly[m] = monthly[m] || { month: m, revenue: 0, expenses: 0, profit: 0 };
    monthly[m].revenue += b.revenue;
    monthly[m].expenses += b.expenses;
    monthly[m].profit += b.profit;
  }
  const chartData = Object.values(monthly).sort((a, b) => a.month.localeCompare(b.month));

  return (
    <div className="space-y-6">
      {/* Range picker */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gg-border bg-gg-surface p-4">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-gg-brown font-semibold">From</span>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-gg-brown font-semibold">To</span>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1" />
        </label>
        <span className="text-xs text-gg-muted">Window: {data.from} → {data.to}</span>
      </div>

      {/* Headline P&L */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card label="Revenue (paid)" value={formatPrice(data.revenue_total)} tone="text-gg-green" />
        <Card label="Revenue (pending)" value={formatPrice(data.revenue_pending_total || 0)} tone="text-[#9b6a26]" subtitle="ClassWallet not yet submitted" />
        <Card label="Expenses" value={formatPrice(data.expenses_total)} tone="text-[#9b6a26]" />
        <Card
          label="Net profit"
          value={formatPrice(data.profit_total)}
          tone={data.profit_total >= 0 ? "text-gg-green" : "text-[#B24C4C]"}
          icon={data.profit_total >= 0 ? TrendingUp : TrendingDown}
        />
      </div>

      {/* Monthly chart */}
      {chartData.length > 0 && (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-4 sm:p-6">
          <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-4">Monthly profit</p>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid stroke="#e7e3da" vertical={false} />
                <XAxis dataKey="month" stroke="#7B5C52" fontSize={11} />
                <YAxis stroke="#7B5C52" fontSize={11} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => formatPrice(v)} />
                <Bar dataKey="revenue" fill="#759F67" name="Revenue" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" fill="#D9933B" name="Expenses" radius={[6, 6, 0, 0]} />
                <Bar dataKey="profit" fill="#7B5C52" name="Profit" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Class P&L */}
      {data.classes?.length > 0 && (
        <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
          <div className="px-4 py-3 border-b border-gg-border bg-gg-bg">
            <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Per class</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gg-bg/60 text-xs uppercase tracking-[0.14em] text-gg-brown">
              <tr><th className="px-4 py-2 text-left">Class</th><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right">Students</th><th className="px-4 py-2 text-right">Revenue</th><th className="px-4 py-2 text-right">Expenses</th><th className="px-4 py-2 text-right">Profit</th></tr>
            </thead>
            <tbody>
              {data.classes.map((c) => (
                <tr key={c.class_id} className="border-t border-gg-border">
                  <td className="px-4 py-2 font-semibold">{c.title}</td>
                  <td className="px-4 py-2 text-xs text-gg-muted">{c.class_date}</td>
                  <td className="px-4 py-2 text-right">{c.students}</td>
                  <td className="px-4 py-2 text-right">{formatPrice(c.revenue)}</td>
                  <td className="px-4 py-2 text-right text-[#9b6a26]">{formatPrice(c.expenses)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${c.profit >= 0 ? "text-gg-green" : "text-[#B24C4C]"}`}>{formatPrice(c.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Booking P&L */}
      {data.bookings?.length > 0 && (
        <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
          <div className="px-4 py-3 border-b border-gg-border bg-gg-bg">
            <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Per 1-on-1 booking</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gg-bg/60 text-xs uppercase tracking-[0.14em] text-gg-brown">
              <tr><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right">Revenue</th><th className="px-4 py-2 text-right">Expenses</th><th className="px-4 py-2 text-right">Profit</th></tr>
            </thead>
            <tbody>
              {data.bookings.map((b) => (
                <tr key={b.booking_id} className="border-t border-gg-border">
                  <td className="px-4 py-2">{b.label}</td>
                  <td className="px-4 py-2 text-xs text-gg-muted">{b.date}</td>
                  <td className="px-4 py-2 text-right">{formatPrice(b.revenue)}</td>
                  <td className="px-4 py-2 text-right text-[#9b6a26]">{formatPrice(b.expenses)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${b.profit >= 0 ? "text-gg-green" : "text-[#B24C4C]"}`}>{formatPrice(b.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Overhead expenses */}
      <OverheadEditor overhead={data.overhead} refetch={refetch} />
    </div>
  );
}

function Card({ label, value, tone = "text-[var(--gg-text)]", icon: Icon, subtitle }) {
  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-5">
      <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">{label}</p>
      <p className={`font-heading text-3xl mt-2 ${tone} inline-flex items-center gap-2`}>
        {Icon ? <Icon size={20} /> : null}
        {value}
      </p>
      {subtitle && <p className="text-[11px] text-gg-muted mt-1 italic">{subtitle}</p>}
    </div>
  );
}

function OverheadEditor({ overhead, refetch }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", category: "marketing", unit_cost: 0, quantity_needed: 1, incurred_on: "", notes: "" });

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/admin/expenses", {
        ...draft,
        scope: "overhead",
        cost_type: "one_time",
        case_size: 1,
        unit_cost: Number(draft.unit_cost) || 0,
        quantity_needed: Number(draft.quantity_needed) || 1,
      });
      toast.success("Expense logged");
      setDraft({ name: "", category: "marketing", unit_cost: 0, quantity_needed: 1, incurred_on: "", notes: "" });
      setAdding(false);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this expense?")) return;
    try { await api.delete(`/admin/expenses/${id}`); refetch(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
      <div className="px-4 py-3 border-b border-gg-border bg-gg-bg flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Overhead & marketing — {formatPrice(overhead?.total || 0)}</p>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white inline-flex items-center gap-1" data-testid="overhead-add-btn">
            <Plus size={12} /> Add expense
          </button>
        )}
      </div>
      {adding && (
        <div className="p-3 border-b border-gg-border bg-gg-bg/50 grid sm:grid-cols-3 gap-2">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name (e.g. Facebook ads)" data-testid="overhead-name" />
          <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="h-9 rounded-md border border-gg-border bg-gg-surface px-2 text-sm">
            {["marketing", "overhead", "supplies", "equipment", "travel", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input value={draft.incurred_on} onChange={(e) => setDraft({ ...draft, incurred_on: e.target.value })} type="date" />
          <Input value={draft.unit_cost} onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value })} type="number" step="0.01" placeholder="Amount ($)" data-testid="overhead-amount" />
          <Input value={draft.quantity_needed} onChange={(e) => setDraft({ ...draft, quantity_needed: e.target.value })} type="number" placeholder="Qty (usually 1)" />
          <Textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={1} placeholder="Notes (optional)" className="sm:col-span-3" />
          <div className="sm:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)} className="text-xs px-3 py-1 rounded-full border border-gg-border">Cancel</button>
            <button type="button" onClick={save} className="text-xs px-3 py-1 rounded-full bg-gg-green text-white" data-testid="overhead-save">Save</button>
          </div>
        </div>
      )}
      {(overhead?.items || []).length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-gg-muted">No overhead expenses recorded yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gg-bg/60 text-xs uppercase tracking-[0.14em] text-gg-brown">
            <tr><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Category</th><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {overhead.items.map((e) => (
              <tr key={e.id} className="border-t border-gg-border">
                <td className="px-4 py-2 font-semibold">{e.name}</td>
                <td className="px-4 py-2 text-xs uppercase tracking-[0.12em] text-gg-brown">{e.category}</td>
                <td className="px-4 py-2 text-xs text-gg-muted">{e.incurred_on || "—"}</td>
                <td className="px-4 py-2 text-right text-[#9b6a26]">{formatPrice(e.line_total)}</td>
                <td className="px-4 py-2 text-right"><button type="button" onClick={() => del(e.id)} className="text-gg-muted hover:text-[#B24C4C]"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
